import { useState } from 'react';
import type { Ref } from 'react';
import { Eye, ExternalLink, BookOpen, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import CalibrationJudgingControls from './CalibrationJudgingControls';
import { formatResearchDuration } from '../../utils/format-research-duration';
import type { JudgingDraft, JudgingHandlers, IdentityState } from './calibration-judging';

export type { Verdict } from '../../constants/calibration-chips';
export type ReasonChip = string;

/**
 * There is deliberately NO `candidateName` on this type, and the resume field
 * is named `redactedResumeText` (the API returns `candidates.sanitizedResumeText`,
 * falling back to an on-read sanitize). Making the leak unrepresentable in the
 * type is the point — a prop that merely isn't rendered comes back.
 */
export interface CalibrationCandidate {
  id: number;
  candidateId: number;
  redactedResumeText: string;
}

interface CalibrationJudgeCardProps {
  candidate: CalibrationCandidate;
  /**
   * US-504a. The draft is OWNED BY `CalibrationJudgingSession`, not by this
   * card. It used to live here as `useState`, reset by the page remounting the
   * card per candidate — that remount still happens, it just happens one level
   * up now, on the session.
   *
   * The reason it moved: the dossier's judging rail renders the same verdict,
   * chip and note. Two copies of a draft is two answers to "what did the
   * recruiter decide?", and the mockup's own correctness demo is typing a note
   * in one place and seeing it in the other.
   */
  draft: JudgingDraft;
  handlers: JudgingHandlers;
  /** The session's single `useCalibrationIdentity`, shared with the dossier. */
  identity: IdentityState;
  /** Chip click submits immediately when true. Owned by the page (persisted). */
  autoAdvance: boolean;
  onAutoAdvanceChange: (next: boolean) => void;
  /** Last unjudged candidate in the round — changes the save button's label. */
  isLast: boolean;
  /**
   * US-492 edit mode: the recruiter stepped BACK to a judgment they already
   * recorded. Two behavioural differences, both deliberate:
   *
   * - auto-advance does not fire. In forward mode a chip tap submits and moves
   *   on, which is the fast path the recruiter asked for; in edit mode that
   *   would re-submit and jump away the moment they touched a chip while
   *   reviewing what they had recorded — losing the undo they just used.
   * - the save button says "Save change", and a Cancel is offered.
   */
  isEditing?: boolean;
  /** Leaves edit mode without writing anything. Required when `isEditing`. */
  onCancel?: () => void;
  /** US-504b. Enters the deliberate reading mode. Also bound to `R`. */
  onOpenDossier: () => void;
  /** So `Esc` can restore focus to the control that opened the dossier. */
  openDossierRef?: Ref<HTMLButtonElement>;
  /**
   * US-503/504e. Seconds in the reading view — the server's record merged with
   * this session's accumulation. Null means it was never opened, which is the
   * state of every fast-path judgment; the marker is absent then, and absent
   * is deliberately not the same as "0 seconds".
   */
  researchSeconds: number | null;
}

/** Item 1 was largely a readability complaint: the text is there, as an unbounded dump. */
const CLAMP_CHARS = 1200;

export default function CalibrationJudgeCard({
  candidate,
  draft,
  handlers,
  identity: identityState,
  autoAdvance,
  onAutoAdvanceChange,
  isLast,
  isEditing = false,
  onCancel,
  onOpenDossier,
  openDossierRef,
  researchSeconds,
}: CalibrationJudgeCardProps) {
  // Stays card-local: the dossier has no clamp (it IS the full read), so this
  // is a card affordance rather than shared draft state.
  const [showFullText, setShowFullText] = useState(false);
  const { identity, isRevealing, error: identityError, onReveal } = identityState;

  const text = candidate.redactedResumeText ?? '';
  const isClamped = !showFullText && text.length > CLAMP_CHARS;
  const shownText = isClamped ? `${text.slice(0, CLAMP_CHARS)}…` : text;
  /**
   * REVIEW FINDING L2. The carried decision for this feature is that "a
   * revealed judgment is never mistaken for a blind one". That was true inside
   * the dossier, which turns amber on reveal — and false HERE, on the card,
   * which is where the fast path actually reveals. A recruiter who revealed
   * from the card and then judged got no standing signal at all.
   *
   * Reveal is set-once server-side and the session remounts per row, so
   * "sighted" is exactly "an identity exists for this candidate".
   */
  const sighted = identity !== null;

  return (
    <div
      data-sighted={sighted}
      className={`rounded-[10px] border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 sm:p-5 ${
        sighted
          ? 'border-amber-400 ring-[3px] ring-amber-400 dark:border-amber-500'
          : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      {/* Non-dismissing on purpose, exactly as in the dossier: "I revealed this
          person before deciding" is a fact about the judgment, not a
          notification about an event. */}
      {sighted && (
        <p className="-mx-4 -mt-4 mb-4 border-b border-amber-400 bg-amber-100 px-4 py-2.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100 sm:-mx-5 sm:-mt-5 sm:px-5">
          <strong className="font-semibold">Sighted from here on.</strong> You revealed this
          candidate&rsquo;s identity before recording a verdict. The judgment still counts — it is
          reported in the sighted bucket, not the blind one.
        </p>
      )}
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="font-semibold">Candidate #{candidate.candidateId}</h3>
        {/* US-504e. Provenance, not decoration: this judgment was made after
            reading the whole file beside the job's requirements, which is what
            makes it interpretable when the back-test disagrees with it. It
            comes off the server row, so it survives a reload and a step back —
            a marker that died on refresh would be a lie about provenance.

            It sits exactly where the `scoreBand` label used to (US-500), so it
            is deliberately a DURATION with units and never a bare number: a
            naked integer beside a candidate on a blind surface reads as a
            rating whatever it actually means. */}
        {researchSeconds !== null && researchSeconds > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400 bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 dark:border-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-200">
            <Check size={12} aria-hidden="true" />
            {`Researched · ${formatResearchDuration(researchSeconds)}`}
          </span>
        )}
      </div>
      {/* US-500. A `scoreBand` label ("top band" / "floor band") used to sit
          beside the heading, above the verdict buttons, on every unjudged
          candidate — the one pre-verdict machine signal on this surface. It is
          gone from the render, from the prop type, and from the wire.
          Deliberately no AI score, star rating, rank, similarity value, match
          count or percentage here either: the API response this component
          consumes carries none of them. And deliberately no name — judging is
          blind on identity too (see below). */}
      <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{shownText}</p>
      {text.length > CLAMP_CHARS && (
        <button
          type="button"
          onClick={() => setShowFullText((s) => !s)}
          className="mt-1 text-sm font-medium text-sky-600 dark:text-sky-400 hover:underline"
        >
          {showFullText ? 'Show less' : 'Show full text'}
        </button>
      )}

      {/* US-504b. The one deliberate keystroke that buys research. It costs the
          nine candidates in twelve who never press it exactly one button on
          screen — the dossier itself is not mounted until this fires. */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          ref={openDossierRef}
          type="button"
          onClick={onOpenDossier}
          className="inline-flex items-center gap-2 rounded-[7px] border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-700 hover:border-sky-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300"
        >
          <BookOpen size={16} aria-hidden="true" />
          Open dossier
          <span
            aria-hidden="true"
            className="rounded border border-b-2 border-gray-300 px-1 text-[10px] font-bold text-gray-500 dark:border-gray-600 dark:text-gray-400"
          >
            R
          </span>
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Full résumé beside the job&rsquo;s requirements. Your place here is held.
        </span>
      </div>

      <div className="mt-4">
        <CalibrationJudgingControls
          draft={draft}
          handlers={handlers}
          prompt="Is this person worth reaching out to?"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handlers.onSave}
          aria-disabled={!draft.verdict}
          className={`rounded px-4 py-2 text-sm font-medium text-white ${
            draft.verdict ? 'bg-sky-600 hover:bg-sky-700' : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
          }`}
        >
          {isEditing ? 'Save change' : isLast ? 'Save & finish' : 'Save & next'}
        </button>
        {isEditing ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
        ) : (
          /* The auto-advance preference is deliberately hidden while editing:
             it has no effect there (see the session's handleChip), and a
             checkbox that does nothing where it is shown is its own small lie. */
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={autoAdvance}
              onChange={(e) => onAutoAdvanceChange(e.target.checked)}
            />
            Save and go to the next candidate when I pick a reason
          </label>
        )}
      </div>

      <div className="mt-5 border-t border-gray-200 dark:border-gray-700 pt-4">
        {!identity ? (
          <>
            <button
              type="button"
              onClick={onReveal}
              className="flex items-center gap-2 rounded border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Eye size={16} />
              {isRevealing ? 'Revealing…' : 'Reveal identity & open in SmartRecruiters'}
            </button>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Judging is blind by default — revealing is recorded.
            </p>
            {identityError && (
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{identityError}</p>
            )}
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium">{identity.candidateName}</span>
            {/* The server builds this URL (`srProfileUrl`) — the client must not
                rebuild it from `srCandidateId`. Two copies of one rule drift,
                and the client's copy is the one nobody tests against the ATS. */}
            {identity.srProfileUrl && (
              <a
                href={identity.srProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sky-600 dark:text-sky-400 hover:underline"
              >
                Open in SmartRecruiters <ExternalLink size={14} />
              </a>
            )}
            <Link
              to={`/candidates/${identity.candidateId}`}
              className="text-sky-600 dark:text-sky-400 hover:underline"
            >
              View in Recruiter Buddy
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export { CALIBRATION_CHIPS } from '../../constants/calibration-chips';
